import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { deviceQueryOptions } from "@/api/media/device-query";
import { useScribeDevice, useScribeStore } from "@/stores/scribe-store";
export function useAudioInputDevices() {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = deviceQueryOptions.audioInputs();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: t1,
    isPending,
    isError,
    error,
    refetch
  } = useQuery(t0);
  let t2;
  if ($[1] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const data = t2;
  let t3;
  if ($[3] !== data || $[4] !== error || $[5] !== isError || $[6] !== isPending || $[7] !== refetch) {
    t3 = {
      audioDevices: data,
      isPending,
      isError,
      error,
      refetch
    };
    $[3] = data;
    $[4] = error;
    $[5] = isError;
    $[6] = isPending;
    $[7] = refetch;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
export function useMicPermission() {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = deviceQueryOptions.micPermission();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: t1,
    isPending,
    isError,
    error,
    refetch
  } = useQuery(t0);
  const data = t1 === undefined ? false : t1;
  let t2;
  if ($[1] !== data || $[2] !== error || $[3] !== isError || $[4] !== isPending || $[5] !== refetch) {
    t2 = {
      micPermissionGranted: data,
      isPending,
      isError,
      error,
      refetch
    };
    $[1] = data;
    $[2] = error;
    $[3] = isError;
    $[4] = isPending;
    $[5] = refetch;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

// Singleton device management: enumerates devices and initializes selection
let mediaDevicesListenerRegistered = false;
async function refreshDevicesAndInitialize() {
  if (typeof navigator === "undefined" || !navigator.mediaDevices) return;
  const devices = await navigator.mediaDevices.enumerateDevices();
  const audioInputs = devices.filter(d => d.kind === "audioinput" && d.deviceId).map(d => ({
    deviceId: d.deviceId,
    label: d.label || `Microphone ${d.deviceId.slice(0, 4)}`
  }));
  const {
    setAudioDevices,
    selectedDevice,
    setSelectedDevice,
    micPermissionGranted
  } = useScribeStore.getState();
  if (!micPermissionGranted) return;
  if (typeof setAudioDevices === "function") {
    setAudioDevices(audioInputs);
  }

  // Validate that the selected device still exists in the available devices
  if (selectedDevice) {
    const deviceExists = audioInputs.some(d => d.deviceId === selectedDevice);
    if (!deviceExists && typeof setSelectedDevice === "function") {
      // Selected device is no longer available, reset to first available or undefined
      const fallback = audioInputs[0];
      setSelectedDevice(fallback?.deviceId ?? undefined);
    }
  } else {
    // No device selected, initialize to first available
    const initial = audioInputs[0];
    if (initial && typeof setSelectedDevice === "function") {
      setSelectedDevice(initial.deviceId);
    }
  }
}
export function useSetupAudioDevices() {
  const {
    micPermissionGranted,
    setMicPermissionGranted,
    audioDevices
  } = useScribeDevice();
  const probedOnceRef = useRef(false);

  // Helper function to check current permission state
  const checkPermissionState = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.permissions) return;
    try {
      const status = await navigator.permissions.query({
        name: "microphone" as PermissionName
      });
      const isGranted = status.state === "granted";
      const wasGranted = useScribeStore.getState().micPermissionGranted;
      setMicPermissionGranted(isGranted);
      // If permission was just granted, refresh devices
      if (isGranted && !wasGranted) {
        await refreshDevicesAndInitialize();
      }
    } catch {
      // Ignore - permissions API might not be supported
    }
  }, [setMicPermissionGranted]);

  // Keep store's micPermissionGranted in sync with browser Permissions API
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions) return;
    let unmounted = false;
    let cleanupFn: (() => void) | undefined;
    (async () => {
      try {
        const status_0 = await navigator.permissions.query({
          name: "microphone" as PermissionName
        });
        if (!unmounted) setMicPermissionGranted(status_0.state === "granted");
        const onChange = () => setMicPermissionGranted(status_0.state === "granted");
        // Safari might not support addEventListener on PermissionStatus
        try {
          status_0.addEventListener?.("change", onChange);
          cleanupFn = () => {
            try {
              status_0.removeEventListener?.("change", onChange);
            } catch {
              // no-op
            }
          };
        } catch {
          // no-op
        }
      } catch {
        // Ignore
      }
    })();
    return () => {
      unmounted = true;
      cleanupFn?.();
    };
  }, [setMicPermissionGranted]);

  // iOS Chrome workaround: Check permissions on visibility change and focus
  // The Permissions API change event doesn't reliably fire on iOS when permissions
  // are changed in Settings, so we poll when the user returns to the page
  useEffect(() => {
    if (typeof document === "undefined" || typeof navigator === "undefined") return;

    // Only apply this workaround on iOS devices when permission is not granted
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
    if (!isIOS || micPermissionGranted) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkPermissionState();
      }
    };
    const handleFocus = () => {
      void checkPermissionState();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Periodic polling as a fallback (every 3 seconds when page is visible)
    // This ensures permission changes are eventually detected even if events don't fire
    const pollInterval = setInterval(() => {
      if (document.visibilityState === "visible") {
        void checkPermissionState();
      }
    }, 3000);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      clearInterval(pollInterval);
    };
  }, [checkPermissionState, micPermissionGranted]);
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) return;

    // Refresh devices if permission is granted
    if (micPermissionGranted) {
      void refreshDevicesAndInitialize();
    }
    if (!mediaDevicesListenerRegistered) {
      const onDeviceChange = () => {
        if (micPermissionGranted) {
          void refreshDevicesAndInitialize();
        }
      };
      navigator.mediaDevices.addEventListener("devicechange", onDeviceChange);
      mediaDevicesListenerRegistered = true;
      return () => {
        if (navigator.mediaDevices?.removeEventListener) {
          try {
            navigator.mediaDevices.removeEventListener("devicechange", onDeviceChange);
          } catch {
            console.error("Error removing media devices listener");
          }
        }
        mediaDevicesListenerRegistered = false;
      };
    }
  }, [micPermissionGranted]);

  // Safari workaround: only probe if permission granted but devices have empty labels
  // This handles Safari's quirk where labels don't populate until getUserMedia is called
  // But we only do it once and only when actually needed (not on every page load)
  useEffect(() => {
    if (!micPermissionGranted) return;
    if (probedOnceRef.current) return;
    // Only probe if we have devices but they all have empty/fallback labels
    // This means getUserMedia hasn't been called yet (Safari case)
    const hasEmptyLabels = audioDevices.length > 0 && audioDevices.every(d => !d.label || d.label.startsWith("Microphone "));
    if (!hasEmptyLabels) return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices) return;
    probedOnceRef.current = true;
    let stream: MediaStream | null = null;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        await refreshDevicesAndInitialize();
      } catch {
        // ignore - permission might have been revoked
      } finally {
        if (stream) {
          stream.getTracks().forEach(t => t.stop());
        }
      }
    })();
  }, [micPermissionGranted, audioDevices]);
}

// High-level controller to manage mic permission, selection, preview stream, and switching
export function useMicrophoneController(config?: {
  enablePreview?: boolean;
}) {
  const {
    enablePreview = false
  } = config ?? {};
  const {
    selectedDevice,
    setSelectedDevice,
    micPermissionGranted,
    setMicPermissionGranted,
    audioDevices
  } = useScribeDevice();
  const switchAudioDevice = useScribeStore.getState().switchAudioDevice;
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const prevSelectedRef = useRef<string | undefined>(selectedDevice);
  const [resetToken, setResetToken] = useState<boolean>(false);
  useSetupAudioDevices();

  // Request mic permission and create an initial preview stream
  const requestMicPermission = useCallback(async (): Promise<boolean> => {
    try {
      if (typeof navigator === "undefined" || !navigator.mediaDevices) {
        setMicPermissionGranted(false);
        return false;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      if (enablePreview) {
        setPreviewStream(prev => {
          if (prev) prev.getTracks().forEach(t => t.stop());
          return stream;
        });
      } else {
        // Stop immediately when not previewing to avoid persistent mic use
        try {
          stream.getTracks().forEach(t_0 => t_0.stop());
        } catch (e) {
          console.error("Error stopping stream:", e);
        }
      }
      setMicPermissionGranted(true);
      // After permission, refresh devices so labels populate and selection initializes
      void refreshDevicesAndInitialize();
      return true;
    } catch (err) {
      console.error("Error requesting microphone access:", err);
      setMicPermissionGranted(false);
      return false;
    }
  }, [setMicPermissionGranted, enablePreview]);

  // Update preview stream whenever selected device changes (and permission granted)
  useEffect(() => {
    if (!enablePreview) return;
    if (!micPermissionGranted) return;
    if (!selectedDevice) return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices) return;

    // Only refresh stream when actual device id changes
    if (prevSelectedRef.current === selectedDevice) return;
    prevSelectedRef.current = selectedDevice;
    navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: {
          exact: selectedDevice
        }
      }
    }).then(newStream => {
      setPreviewStream(prev_0 => {
        if (prev_0) prev_0.getTracks().forEach(t_1 => t_1.stop());
        return newStream;
      });
    }).catch(error => {
      console.error("Error accessing selected device:", error);
    });
  }, [selectedDevice, micPermissionGranted, enablePreview]);

  // Clean up preview stream on unmount
  useEffect(() => {
    if (!enablePreview) return;
    return () => {
      setPreviewStream(prev_1 => {
        if (prev_1) prev_1.getTracks().forEach(t_2 => t_2.stop());
        return null;
      });
    };
  }, [enablePreview]);
  const selectDevice = useCallback(async (deviceId: string, opts?: {
    mimeType?: string;
    chunkMs?: number;
  }) => {
    if (!deviceId || deviceId === selectedDevice) return;
    setSelectedDevice(deviceId);
    // If actively recording, switch seamlessly; store handles restarting
    try {
      if (typeof switchAudioDevice === "function") {
        await switchAudioDevice(deviceId, opts);
      }
    } finally {
      setResetToken(prev => !prev);
    }
  }, [selectedDevice, setSelectedDevice, switchAudioDevice]);
  return {
    // state
    audioDevices,
    selectedDevice,
    micPermissionGranted,
    previewStream,
    inputLevelResetToken: resetToken,
    // actions
    requestMicPermission,
    selectDevice
  } as const;
}